import { useState, useCallback, useMemo } from 'react';
import { useClient } from 'jsonapi-react';
import api from '@/services/api';

export const useFlashcardSession = () => {
  const client = useClient();

  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  const [lastFeedback, setLastFeedback] = useState(null);

  const currentCard = useMemo(() => cards[currentIndex] || null, [cards, currentIndex]);

  const progress = useMemo(
    () => ({
      current: currentIndex + 1,
      total: cards.length,
      percentage: cards.length > 0 ? (currentIndex / cards.length) * 100 : 0
    }),
    [currentIndex, cards.length]
  );

  const correctCount = useMemo(() => answers.filter(a => a.correct).length, [answers]);

  const wrongCount = useMemo(() => answers.filter(a => !a.correct).length, [answers]);

  const fetchSession = useCallback(
    async (limit = 10) => {
      setIsLoading(true);
      setError(null);
      setIsComplete(false);
      setAnswers([]);
      setCurrentIndex(0);
      setLastFeedback(null);

      try {
        const response = await client.fetch(`flashcards/session?limit=${limit}`);

        if (response.error) {
          throw new Error(response.error.message || 'Erro ao carregar flashcards');
        }

        const flashcards = response.data || [];
        setCards(flashcards);
        // Não setar isComplete aqui - deixar o componente mostrar a mensagem "Nenhum flashcard disponível"
      } catch (err) {
        setError(err.message || 'Erro ao carregar sessão de flashcards');
        setCards([]);
      } finally {
        setIsLoading(false);
      }
    },
    [client]
  );

  const submitAnswer = useCallback(async (flashcardId, remembered) => {
    setIsLoading(true);
    setError(null);

    try {
      // Use direct API call (not jsonapi-react) since backend expects plain JSON
      const { data } = await api.post('/flashcard_responses', {
        data: {
          attributes: {
            'flashcard-id': flashcardId,
            remembered
          }
        }
      });

      const answerResult = {
        flashcardId,
        remembered,
        correct: data.attributes.correct,
        correctAnswer: data.attributes['correct-answer'],
        explanation: data.attributes.explanation,
        newRetention: data.attributes['new-retention'],
        nextReviewIn: data.attributes['next-review-in']
      };

      setAnswers(prev => [...prev, answerResult]);
      setLastFeedback(answerResult);

      return answerResult;
    } catch (err) {
      setError(err.message || 'Erro ao enviar resposta');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const nextCard = useCallback(() => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setLastFeedback(null);
    } else {
      setIsComplete(true);
    }
  }, [currentIndex, cards.length]);

  const resetSession = useCallback(() => {
    setCards([]);
    setCurrentIndex(0);
    setAnswers([]);
    setIsComplete(false);
    setError(null);
    setLastFeedback(null);
  }, []);

  const getResults = useCallback(
    () => ({
      totalCards: cards.length,
      correctCount,
      wrongCount,
      accuracy: cards.length > 0 ? (correctCount / cards.length) * 100 : 0,
      answers
    }),
    [cards.length, correctCount, wrongCount, answers]
  );

  return {
    cards,
    currentCard,
    currentIndex,
    answers,
    isLoading,
    isComplete,
    error,
    lastFeedback,
    progress,
    correctCount,
    wrongCount,
    fetchSession,
    submitAnswer,
    nextCard,
    resetSession,
    getResults
  };
};

export default useFlashcardSession;
